import cornerstoneTools from "cornerstone-tools";
import {
  clampFluidManualBrushRadius,
  FLUID_ROW_LINE_WIDTH_IMAGE_PX,
} from "../../utils/fluidManualBrush";
import { getFluidPayloadEndpointsForFrame } from "../../utils/fluidFrameState";
import store from "../../../../store";
const BaseAnnotationTool = cornerstoneTools.importInternal(
  "base/BaseAnnotationTool"
);

const external = cornerstoneTools.external;

const draw = cornerstoneTools.importInternal("drawing/draw");

const getNewContext = cornerstoneTools.importInternal("drawing/getNewContext");

const getToolState = cornerstoneTools.getToolState;

const throttle = cornerstoneTools.importInternal("util/throttle");
const rectangleRoiCursor = cornerstoneTools.importInternal("cursors/index");
//const numbersWithCommas = cornerstoneTools.importInternal('util/numbersWithCommas');

/**
 * @public
 * @class RectangleStatic
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing rectangular regions of interest, and measuring
 * the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class FluidSegmentationTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "FluidSegmentation",
      supportedInteractionTypes: [],
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        // showMinMax: false,
        // showHounsfieldUnits: true
      },
      svgCursor: rectangleRoiCursor,
    };

    super(props, defaultProps);

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return;
    }

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        initialRotation: eventData.viewport.rotation,
      },
    };
  }

  renderToolData(evt) {
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const eventData = evt.detail;
    const { element, canvasContext } = eventData;
    const context = getNewContext(canvasContext.canvas);

    /** Per-color stroke layers for horizontal fluid row lines (0.5 image px). */
    const rowLineLayers = new Map(); // color -> { segments: [], alpha }
    /** Legacy polyline data (AI / saved annotations). */
    const polylineFillLayers = new Map();
    /** Manual brush draft polyline preview (round cap stroke). */
    const draftStrokeLayers = [];

    const rowLineWidthCanvas = (() => {
      const p0 = external.cornerstone.pixelToCanvas(element, { x: 0, y: 0 });
      const pHalf = external.cornerstone.pixelToCanvas(element, {
        x: 0,
        y: FLUID_ROW_LINE_WIDTH_IMAGE_PX,
      });
      return Math.max(0.5, Math.hypot(pHalf.x - p0.x, pHalf.y - p0.y));
    })();

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }

      const color = data.color || "yellow";
      const alpha = typeof data.alpha === "number" ? data.alpha : 0.3;

      if (data.start && data.end) {
        if (!rowLineLayers.has(color)) {
          rowLineLayers.set(color, { segments: [], alpha });
        }
        const layer = rowLineLayers.get(color);
        layer.alpha = alpha;
        layer.segments.push({
          x1: Math.min(data.start.x, data.end.x),
          x2: Math.max(data.start.x, data.end.x),
          y: data.start.y,
        });
      } else if (Array.isArray(data.points) && data.points.length > 1) {
        if (!polylineFillLayers.has(color)) {
          polylineFillLayers.set(color, { path: new Path2D(), alpha });
        }
        const entry = polylineFillLayers.get(color);
        entry.alpha = alpha;

        const pts =
          data.preserveStrokeOrder === true
            ? data.points.slice()
            : data.points.slice().sort((a, b) => a.x - b.x);
        const first = external.cornerstone.pixelToCanvas(element, pts[0]);
        const strokePath = new Path2D();
        strokePath.moveTo(first.x, first.y);
        for (let j = 1; j < pts.length; j++) {
          const p = external.cornerstone.pixelToCanvas(element, pts[j]);
          strokePath.lineTo(p.x, p.y);
        }
        const last = pts[pts.length - 1];
        const isClosed = pts[0].x === last.x && pts[0].y === last.y;

        if (data._manualFluidDraft) {
          const r = clampFluidManualBrushRadius(
            typeof data.draftStrokeRadius === "number"
              ? data.draftStrokeRadius
              : undefined
          );
          const dPx = 2 * r;
          const p0 = external.cornerstone.pixelToCanvas(element, {
            x: 0,
            y: 0,
          });
          const pD = external.cornerstone.pixelToCanvas(element, {
            x: dPx,
            y: 0,
          });
          const lineWidth = Math.max(
            1,
            Math.hypot(pD.x - p0.x, pD.y - p0.y)
          );
          draftStrokeLayers.push({ path: strokePath, color, alpha, lineWidth });
        } else {
          entry.path.addPath(strokePath);
          if (isClosed) {
            entry.path.closePath();
          }
        }
      }
    }

    draw(context, (ctx) => {
      for (const [color, { segments, alpha }] of rowLineLayers.entries()) {
        const previousAlpha = ctx.globalAlpha;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.lineWidth = rowLineWidthCanvas;
        ctx.lineCap = "butt";
        ctx.lineJoin = "miter";
        ctx.beginPath();
        segments.forEach(({ x1, x2, y }) => {
          const rowCenterY = y + 0.5;
          const startCanvas = external.cornerstone.pixelToCanvas(element, {
            x: x1,
            y: rowCenterY,
          });
          const endCanvas = external.cornerstone.pixelToCanvas(element, {
            x: x2 + 1,
            y: rowCenterY,
          });
          ctx.moveTo(startCanvas.x, startCanvas.y);
          ctx.lineTo(endCanvas.x, endCanvas.y);
        });
        ctx.stroke();
        ctx.globalAlpha = previousAlpha;
      }

      for (const [color, { path, alpha }] of polylineFillLayers.entries()) {
        const previousAlpha = ctx.globalAlpha;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.lineWidth = rowLineWidthCanvas;
        ctx.stroke(path);
        ctx.globalAlpha = previousAlpha;
      }

      for (const { path, color, alpha, lineWidth } of draftStrokeLayers) {
        const previousAlpha = ctx.globalAlpha;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke(path);
        ctx.globalAlpha = previousAlpha;
      }

      const reduxState = store.getState()?.dcm;
      if (reduxState?.fluidPayloadPointsVisible) {
        const stackState = cornerstoneTools.getToolState(element, "stack");
        const frameIndex =
          stackState?.data?.[0]?.currentImageIdIndex ??
          reduxState?.currentFrame ??
          0;
        const stackId = new URLSearchParams(window.location.search).get(
          "stack_id"
        );
        const imageIds =
          reduxState?.dicom_data?.[stackId]?.OPT?.stack?.imageIds || [];

        const endpoints = getFluidPayloadEndpointsForFrame(
          imageIds,
          frameIndex,
          reduxState?.fluidSegmentation
        );

        endpoints.forEach((point) => {
          const canvasPt = external.cornerstone.pixelToCanvas(element, {
            x: point.x,
            y: point.y,
          });
          ctx.beginPath();
          ctx.arc(canvasPt.x, canvasPt.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = point.color;
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#ffffff";
          ctx.stroke();
        });
      }
    });
  }
}
